import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, GraduationCap, FileBadge, ArrowRight, Award } from "lucide-react";

const benefits = [
  {
    icon: ShieldCheck,
    title: "Riconoscimento ufficiale",
    text:
      "Iscrizione al Registro come Educatore e Consulente Digitale, Tecnologo e di Sostenibilità tramite A.C.D.T., associazione regolamentata dalla Legge 4/2013.",
  },
  {
    icon: GraduationCap,
    title: "Percorso formativo strutturato",
    text:
      "Un programma che integra metodo organizzativo, cybersicurezza, leadership e sostenibilità digitale — coerente con il Metodo di Mare.",
  },
  {
    icon: FileBadge,
    title: "Tutela professionale",
    text:
      "Una figura professionale certificata, riconosciuta e tutelata dalla legge. La professione del futuro, con le fondamenta giuste.",
  },
];

export default function Certification() {
  return (
    <section
      id="certification"
      data-testid="certification-section"
      className="relative py-24 md:py-32 border-t border-[#E5E1DA] bg-gradient-to-br from-[#0B192C] via-[#1E3557] to-[#0E7C8F] text-[#F9F8F6] overflow-hidden"
    >
      {/* Decorative orbs */}
      <div
        aria-hidden
        className="absolute -top-32 -right-20 w-[40%] h-[60%] opacity-30 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #E89B6C 0%, transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="absolute bottom-0 -left-20 w-[35%] h-[55%] opacity-20 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #6EC1D9 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          {/* Left: heading + manifesto */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="md:col-span-6"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#E89B6C]/40 bg-[#E89B6C]/10 mb-6">
              <Award size={14} className="text-[#E89B6C]" strokeWidth={1.4} />
              <span className="overline text-[#E89B6C]">Legge 4/2013</span>
            </div>

            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
              Diventa un
              <br />
              professionista
              <br />
              <em className="text-[#E89B6C]">certificato.</em>
            </h2>

            <p className="mt-8 text-lg font-light leading-relaxed text-[#F9F8F6]/85 max-w-xl">
              Metodo di Mare è anche la{" "}
              <strong className="text-[#F9F8F6] font-normal">
                porta di accesso al percorso ufficiale
              </strong>{" "}
              per diventare{" "}
              <em>Consulente ed Educatore Digitale, Tecnologico e di Sostenibilità</em>
              .
            </p>
            <p className="mt-4 text-base font-light text-[#F9F8F6]/70">
              Maggiori informazioni sull'associazione:{" "}
              <a
                href="https://www.assoacdt.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="certification-acdt-link"
                className="text-[#E89B6C] underline underline-offset-4 hover:text-[#F9F8F6] transition-colors"
              >
                www.assoacdt.com
              </a>
            </p>

            {/* Credentials row: ACDT logo + Official certificate */}
            <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-xl">
              {/* ACDT identity */}
              <div className="bg-[#F9F8F6] border border-[#F9F8F6]/15 p-5 flex flex-col items-start gap-3">
                <img
                  src="https://customer-assets.emergentagent.com/job_mare-method-shop-1/artifacts/h5elhbpc_ACDT_logo_800x520.jpg"
                  alt="Logo A.C.D.T."
                  className="h-14 w-auto object-contain"
                  data-testid="acdt-logo"
                />
                <div>
                  <p className="overline text-[#E89B6C]">Associazione</p>
                  <p className="font-serif-display text-lg mt-0.5 leading-tight text-[#0B192C]">
                    A.C.D.T.
                  </p>
                  <p className="text-[10px] text-[#3A4F6B] mt-1 leading-snug">
                    Consulenti e Consumatori Digitali Tecnologici e di Sostenibilità
                  </p>
                </div>
              </div>

              {/* Official certificate */}
              <a
                href="https://customer-assets.emergentagent.com/job_mare-method-shop-1/artifacts/ude223t6_Immagine%202026-06-20%20104050%20-%20Copia.png"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="acdt-certificate"
                className="group bg-[#F9F8F6] border border-[#F9F8F6]/15 p-5 flex flex-col items-start gap-3 hover:border-[#E89B6C] transition-colors relative"
              >
                <div className="relative w-full h-14 flex items-center justify-start overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_mare-method-shop-1/artifacts/ude223t6_Immagine%202026-06-20%20104050%20-%20Copia.png"
                    alt="Attestato A.C.D.T. — Michele Roccaro"
                    className="h-20 w-auto object-contain"
                    loading="lazy"
                  />
                </div>
                <div>
                  <p className="overline text-[#E89B6C]">Attestato ufficiale</p>
                  <p className="font-serif-display text-lg mt-0.5 leading-tight text-[#0B192C]">
                    Consulente digitale
                  </p>
                  <p className="text-[10px] text-[#3A4F6B] mt-1 leading-snug">
                    N° B0196-…0114D · Valido 13/01/2027 · L. 4/2013
                  </p>
                </div>
                <span className="absolute top-2 right-2 text-[9px] uppercase tracking-[0.18em] text-[#3A4F6B] opacity-0 group-hover:opacity-100 transition-opacity">
                  Visualizza ↗
                </span>
              </a>
            </div>

            <a
              href="https://wa.me/393773660476?text=Ciao%20Michele%2C%20vorrei%20informazioni%20sul%20percorso%20di%20certificazione%20A.C.D.T."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="certification-cta"
              className="group mt-10 inline-flex items-center gap-3 px-8 py-4 bg-[#E89B6C] text-[#0B192C] text-sm uppercase tracking-[0.2em] hover:bg-[#F9F8F6] transition-all"
            >
              Richiedi informazioni
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
          </motion.div>

          {/* Right: benefits */}
          <div className="md:col-span-5 md:col-start-8 space-y-6">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.12 }}
                  className="border-l-2 border-[#E89B6C] pl-6 py-2"
                  data-testid={`certification-benefit-${i}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <Icon size={20} strokeWidth={1.3} className="text-[#E89B6C]" />
                    <h3 className="font-serif-display text-2xl text-[#F9F8F6]">
                      {b.title}
                    </h3>
                  </div>
                  <p className="text-base font-light leading-relaxed text-[#F9F8F6]/80">
                    {b.text}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Bottom strip */}
        <div className="mt-20 pt-8 border-t border-[#F9F8F6]/15 grid sm:grid-cols-3 gap-6 text-center sm:text-left">
          <div>
            <p className="overline text-[#E89B6C]">Iscrizione</p>
            <p className="font-serif-display text-2xl mt-2">Registro A.C.D.T.</p>
          </div>
          <div>
            <p className="overline text-[#E89B6C]">Quadro normativo</p>
            <p className="font-serif-display text-2xl mt-2">Legge 4 / 2013</p>
          </div>
          <div>
            <p className="overline text-[#E89B6C]">Area</p>
            <p className="font-serif-display text-2xl mt-2">
              Digitale · Sostenibilità
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
